import instantsearch from 'instantsearch.js';
import {
  configure,
  searchBox,
  hits,
  clearRefinements,
  refinementList,
  stats,
  pagination,
} from 'instantsearch.js/es/widgets';

import searchClient from './search-client';
import { createDropdown } from './vendor/dropdown';
import Hit from './templates/hits';

/**
 * GlobalSearch
 */
class GlobalSearch {
  constructor(rootEl) {
    this.rootEl = rootEl;
    if (!this.rootEl) {
      throw new Error('Root search element not found.');
    }

    this.hitsPerPage = 20;

    this.indexName = this.rootEl.dataset['algoliaIndex'];

    this.resultsLabel = {
      singular: 'result',
      plural: 'results',
    };

    this.searchParams = {
      hitsPerPage: this.hitsPerPage,
      distinct: true,
      // Force faceting to be applied after de-duplication (via the Distinct setting)
      facetingAfterDistinct: true,
    };

    this.search = instantsearch({
      indexName: this.indexName,
      searchClient: searchClient,
      routing: true,
    });
  }

  /**
   * Initialize the search
   */
  init() {
    this.configureWidgets();
    this.setWidgets();
    this.addWidgets();
    this.search.start();
  }

  /**
   * Configure search widgets and add them as instance variables
   */
  configureWidgets() {
    this.configureSearchParams();
    this.configureSearchBox();
    this.configureStats();
    this.configureClearRefinements();
    this.configureDepartmentRefinementDropdown();
    this.configureImpactAreaRefinementDropdown();
    this.configureContentTypeRefinementDropdown();
    this.configureResultsList();
    this.configurePagination();
  }

  /**
   * Set widgets array
   */
  setWidgets() {
    this.widgets = [
      this.configureSearch,
      this.searchBox,
      this.stats,
      this.clearRefinements,
      this.resultsList,
      this.pagination,
    ];

    if (this.departmentRefinementDropdown) {
      this.widgets.push(this.departmentRefinementDropdown);
    }

    if (this.impactAreaRefinementDropdown) {
      this.widgets.push(this.impactAreaRefinementDropdown);
    }

    if (this.contentTypeRefinementDropdown) {
      this.widgets.push(this.contentTypeRefinementDropdown);
    }
  }

  /**
   * Add widgets to the search instance
   */
  addWidgets() {
    this.search.addWidgets(this.widgets);
  }

  /**
   * Configure search parameters
   */
  configureSearchParams() {
    this.configureSearch = configure(this.searchParams);
  }

  /**
   * Configure the search box
   */
  configureSearchBox() {
    this.searchBox = searchBox({
      container: '.js-search-input',
      placeholder: 'Search',
      showSubmit: false,
    });
  }

  /**
   * Configure the search stats widget
   */
  configureStats() {
    this.stats = stats({
      container: '.js-search-stats',
      templates: {
        text: ({ nbHits }) => {
          const { singular, plural } = this.resultsLabel;
          return `Showing ${nbHits} ${nbHits === 1 ? singular : plural} ${
            this.department ? ` in ${this.department}` : ''
          }`;
        },
      },
    });
  }

  /**
   * Configure the clear refinements widget
   */
  configureClearRefinements() {
    this.clearRefinements = clearRefinements({
      container: '.js-search-clear',
      templates: {
        resetLabel(_options, { html }) {
          return html`Reset filters`;
        },
      },
    });
  }

  /**
   * Configure the department refinement dropdown
   */
  configureDepartmentRefinementDropdown() {
    if (!document.querySelector('.js-search-departments-input')) {
      return;
    }

    const departmentDropdown = createDropdown(refinementList, {
      cssClasses: { root: 'ais-Dropdown-departments' },
      buttonText: 'All Departments',
    });

    this.departmentRefinementDropdown = departmentDropdown({
      container: '.js-search-departments-input',
      attribute: 'departments.name',
      limit: 15,
      showMore: true,
      showMoreLimit: 100,
      sortBy: ['name:asc'],
    });
  }

  /**
   * Configure the impact area refinement dropdown
   */
  configureImpactAreaRefinementDropdown() {
    if (!document.querySelector('.js-search-impact-area-input')) {
      return;
    }

    const impactAreaDropdown = createDropdown(refinementList, {
      cssClasses: { root: 'ais-Dropdown-impact-area' },
      buttonText: 'All Impact Areas',
    });

    this.impactAreaRefinementDropdown = impactAreaDropdown({
      container: '.js-search-impact-area-input',
      attribute: 'impact_areas',
      limit: 10,
      showMore: true,
      showMoreLimit: 100,
      sortBy: ['name:asc'],
    });
  }

  /**
   * Configure the content type refinement dropdown
   */
  configureContentTypeRefinementDropdown() {
    if (!document.querySelector('.js-search-content-type-input')) {
      return;
    }

    const contentTypeDropdown = createDropdown(refinementList, {
      cssClasses: { root: 'ais-Dropdown-content-type' },
      buttonText: 'All Content Types',
    });

    this.contentTypeRefinementDropdown = contentTypeDropdown({
      container: '.js-search-content-type-input',
      attribute: 'type',
      limit: 10,
      showMoreLimit: 100,
      sortBy: ['name:asc'],
      templates: {
        item(item, { html }) {
          const { count, label, value, isRefined } = item;

          return html` <label class="ais-RefinementList-label">
            <input
              type="checkbox"
              class="ais-RefinementList-checkbox"
              value="${value}"
              checked="${isRefined}" />
            <span class="ais-RefinementList-labelText">${label}</span>
            <span class="ais-RefinementList-count">${count}</span>
          </label>`;
        },
      },
      transformItems: items =>
        items.map(item => {
          if (item.value === 'post') {
            item.label = 'News';
          } else {
            item.label = item.label.replace(/^./, item.label[0].toUpperCase());
          }
          return item;
        }),
    });
  }

  /**
   * Configure the results list
   */
  configureResultsList() {
    this.resultsList = hits({
      container: '.js-search-hits',
      templates: {
        /* eslint-disable */
        item: (hit, { html }) => {
          const newHit = new Hit(hit.type, hit);
          return newHit.template(html);
        },

        empty(results, { html }) {
          return html`<p role="alert">No results</p>`;
        },
        /* eslint-enable */
      },
    });
  }

  /**
   * Configure the pagination
   */
  configurePagination() {
    this.pagination = pagination({
      container: '.js-search-pagination',
      showFirst: false,
      showLast: false,
      padding: 2,
      scrollTo: '.js-search-hits',
      templates: {
        /* eslint-disable */
        previous({}, { html }) {
          return html` <span>Previous</span> `;
        },
        next({}, { html }) {
          return html` <span>Next</span> `;
        },
        /* eslint-enable */
      },
    });
  }
}

export default GlobalSearch;
